import React from 'react';
import { AppData } from '../types';
import { AlertCircle, Clock, ExternalLink, Smartphone } from 'lucide-react';
import { useI18n } from '../../services/i18n';

interface GooglePlayTabProps {
  app: AppData;
}

export const GooglePlayTab: React.FC<GooglePlayTabProps> = ({ app }) => {
  const { t } = useI18n();

  const googlePlayPackage = app.bundleId || `com.${app.developerSlug}.${app.slug}`;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Notice Banner */}
      <div className="bg-amber-50/80 rounded-3xl p-6 border border-amber-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-wider text-amber-800 bg-amber-200/60 px-2 py-0.5 rounded-full">
                {t('profile.googlePlayCached')}
              </span>
            </div>
            <h3 className="text-base font-bold text-slate-900 mt-1">
              Google Play Ingestion Pending
            </h3>
            <p className="text-xs text-amber-900/80 mt-0.5 max-w-xl">
              Displaying cross-referenced public identifier records. Full Google Play Store API provider integration is awaiting scheduled feed rollout. No simulated metrics are displayed.
            </p>
          </div>
        </div>

        <span className="px-3 py-1.5 rounded-xl bg-white border border-amber-200 text-amber-800 text-xs font-semibold self-start sm:self-center shadow-2xs">
          Cross-Store Reference
        </span>
      </div>

      {/* Cached Info Grid */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-6">
        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
          Known Google Play Technical Cross-Reference
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Package ID
            </span>
            <span className="text-xs font-mono font-bold text-slate-900 block truncate">
              {googlePlayPackage}
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Android Platform Status
            </span>
            <span className="text-xs font-semibold text-emerald-700 block">
              Cross-Platform Available
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Publisher Name
            </span>
            <span className="text-sm font-semibold text-slate-900 block truncate">
              {app.developer}
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Category Mapping
            </span>
            <span className="text-sm font-semibold text-slate-900 block">
              {app.category}
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Data Ingestion Source
            </span>
            <span className="text-xs font-semibold text-slate-600 block">
              Cached / Cross-Store Matching
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Target Architecture
            </span>
            <span className="text-xs font-semibold text-slate-600 block">
              Android 10.0+ (API 29+)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
